import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useApiClient } from '../config/axios';

export interface Customer {
  tenant: string;
  environment: string;
  customer_name: string;
  created_at?: string;
}

export const customerKey = (c: Pick<Customer, 'tenant' | 'environment'>): string =>
  `${c.tenant}:${c.environment}`;

interface CustomerContextType {
  currentCustomer: Customer | null;
  availableCustomers: Customer[];
  isLoading: boolean;
  setCustomer: (tenant: string, environment: string, userId: string) => Promise<void>;
  loadCustomers: () => Promise<void>;
  loadLastSelected: (userId: string) => Promise<Customer | null>;
  clearCustomer: () => void;
}

const CustomerContext = createContext<CustomerContextType | undefined>(undefined);

export const useCustomer = (): CustomerContextType => {
  const context = useContext(CustomerContext);
  if (!context) {
    throw new Error('useCustomer must be used within a CustomerProvider');
  }
  return context;
};

interface CustomerProviderProps {
  children: ReactNode;
}

export const CustomerProvider: React.FC<CustomerProviderProps> = ({ children }) => {
  const apiClient = useApiClient();
  const [currentCustomer, setCurrentCustomer] = useState<Customer | null>(null);
  const [availableCustomers, setAvailableCustomers] = useState<Customer[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Load customer from localStorage on mount
  useEffect(() => {
    const storedCustomer = localStorage.getItem('currentCustomer');
    if (storedCustomer) {
      try {
        const customer = JSON.parse(storedCustomer);
        setCurrentCustomer(customer);
      } catch (error) {
        console.error('Failed to parse stored customer:', error);
        localStorage.removeItem('currentCustomer');
      }
    } else {
      // Ensure state is null if nothing in localStorage
      setCurrentCustomer(null);
    }
  }, []);

  // Periodically check if localStorage was cleared (e.g., by logout)
  useEffect(() => {
    const checkInterval = setInterval(() => {
      const storedCustomer = localStorage.getItem('currentCustomer');
      if (!storedCustomer && currentCustomer) {
        setCurrentCustomer(null);
      }
    }, 100); // Check every 100ms

    return () => clearInterval(checkInterval);
  }, [currentCustomer]);

  const loadCustomers = async () => {
    try {
      setIsLoading(true);
      const response = await apiClient.get('/customers');
      if (response.data.success) {
        setAvailableCustomers(response.data.customers);
      }
    } catch (error) {
      console.error('Failed to load customers:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const loadLastSelected = async (userId: string): Promise<Customer | null> => {
    try {
      const response = await apiClient.get(`/customers/last-selected/${userId}`);
      if (response.data.success && response.data.customer) {
        return response.data.customer;
      }
      return null;
    } catch (error) {
      console.error('Failed to load last selected customer:', error);
      return null;
    }
  };

  const setCustomer = async (tenant: string, environment: string, userId: string) => {
    try {
      setIsLoading(true);

      const response = await apiClient.post('/customers/select', {
        tenant,
        environment,
        user_id: userId
      });

      if (response.data.success) {
        const customer = response.data.customer;

        setCurrentCustomer(customer);
        localStorage.setItem('currentCustomer', JSON.stringify(customer));

        // Reload the page so every component picks up the new tenant context.
        window.location.reload();
      }
    } catch (error) {
      console.error('Failed to select customer:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const clearCustomer = () => {
    setCurrentCustomer(null);
    localStorage.removeItem('currentCustomer');
  };

  const value: CustomerContextType = {
    currentCustomer,
    availableCustomers,
    isLoading,
    setCustomer,
    loadCustomers,
    loadLastSelected,
    clearCustomer
  };

  return (
    <CustomerContext.Provider value={value}>
      {children}
    </CustomerContext.Provider>
  );
};
